import { useEffect, useState } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@heroui/react';
import { Button } from '../Button';
import { cn } from '../../../lib/cn';

// NextUI's <Modal> relies on framer-motion's <AnimatePresence> to play the
// exit animation and then unmount its portal. Under certain conditions
// (headless Chrome, rapid open/close, nested Tabs/Suspense) the exit-complete
// callback never fires, so the portal wrapper stays in the DOM at opacity:0
// with pointer-events:auto, blocking every click underneath.
// See https://github.com/heroui-inc/heroui/issues/4561 and
// https://github.com/heroui-inc/heroui/issues/5734.
//
// To guarantee cleanup we keep the <Modal> mounted for a short grace period
// after `open` flips to false (so the exit animation can still play) and then
// force-unmount it. The 350ms window covers both the wrapper exit (0.2s) and
// the backdrop fade exit (0.3s) defined below.
const EXIT_GRACE_MS = 350;

export const CustomModal = ({
  title,
  children,
  open,
  close,
  // Primary Button Properties
  showPrimaryButton = true,
  primaryButtonText,
  primaryButtonClick,
  primaryButtonVariant = "filled",
  primaryButtonColor = "primary",
  primaryButtonLoading = false,
  primaryButtonDisabled = false,
  showCloseButton = true,
  showHeaderCancelButton = false,

  // Secondary Button Properties
  showSecondaryButton = true,
  secondaryButtonText,
  secondaryButtonClick,
  secondaryButtonVariant = "outlined",
  secondaryButtonColor = "primary",
  secondaryButtonLoading = false,
  secondaryButtonDisabled = false,

  // Overlay Clickable Property
  isDismissable = false,
  dialogSize = "default",
}) => {
  // Mount the NextUI <Modal> while open and for a brief grace window after
  // close, then force-unmount to guarantee the portal is removed even when
  // framer-motion's exit-complete callback never fires.
  const [mounted, setMounted] = useState(open);

  useEffect(() => {
    if (open) {
      setMounted(true);
      return;
    }
    const t = setTimeout(() => setMounted(false), EXIT_GRACE_MS);
    return () => clearTimeout(t);
  }, [open]);

  if (!mounted) return null;

  const modalSizeClass = dialogSize === "xlarge" ? "max-w-[1200px]" : "min-w-[300px] max-w-[700px]";
  return (
    <Modal
      backdrop='blur'
      placement='center'
      isOpen={open}
      onClose={close}
      closeButton={showCloseButton}
      isDismissable={isDismissable}
      // Escape should always dismiss, even when isDismissable=false blocks
      // backdrop clicks (NextUI forwards this to react-aria's useOverlay).
      isKeyboardDismissDisabled={false}
      className={cn("select-none shadow-2xl rounded-2xl border border-surface-border", modalSizeClass)}
      classNames={{
        backdrop: 'bg-black/40 backdrop-blur-sm z-modal',
        wrapper: 'z-modal overflow-y-auto',
        closeButton: 'top-4 right-4 text-mediumgrey hover:text-darkgrey hover:bg-surface-hover transition-colors rounded-full p-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'
      }}
      motionProps={{
        variants: {
          enter: {
            scale: 1,
            opacity: 1,
            y: 0,
            transition: {
              duration: 0.3,
              ease: "easeOut",
            },
          },
          exit: {
            scale: 0.95,
            opacity: 0,
            y: 10,
            transition: {
              duration: 0.2,
              ease: "easeIn",
            },
          },
        }
      }}
    >
      <ModalContent className="bg-white max-h-[90vh] flex flex-col overflow-hidden">
        {() => (
          <>
            <ModalHeader className='px-6 pt-6 pb-2 flex flex-shrink-0 items-center justify-between gap-4'>
              <div className='font-sans'>
                <h2 className='text-xl font-bold text-primary'>{title}</h2>
              </div>
              {showHeaderCancelButton ? (
                <Button
                  variant="transparent"
                  color="primary"
                  size="sm"
                  className="shrink-0"
                  onClick={close}
                >
                  Cancel
                </Button>
              ) : null}
            </ModalHeader>
            <ModalBody className="px-6 py-4 overflow-y-auto custom-scrollbar flex-1 min-h-0">
              {children}
            </ModalBody>
            {(showSecondaryButton || showPrimaryButton) && (
              <ModalFooter className='px-6 py-4 border-t border-surface-border flex justify-end gap-3 flex-shrink-0'>
                {showSecondaryButton ? (
                  <Button
                    className='w-full md:w-auto min-w-[120px] font-medium'
                    variant={secondaryButtonVariant}
                    color={secondaryButtonColor}
                    disabled={secondaryButtonDisabled}
                    loading={secondaryButtonLoading}
                    onClick={secondaryButtonClick || close}
                  >
                    {secondaryButtonText || 'Cancel'}
                  </Button>
                ) : null}

                {showPrimaryButton ? (
                  <Button
                    className='w-full md:w-auto min-w-[120px] font-medium shadow-sm hover:shadow-md transition-shadow'
                    variant={primaryButtonVariant}
                    disabled={primaryButtonDisabled}
                    color={primaryButtonColor}
                    loading={primaryButtonLoading}
                    onClick={primaryButtonClick || close}
                  >
                    {primaryButtonText || 'Save'}
                  </Button>
                ) : null}
              </ModalFooter>
            )}
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
